import { action, observable } from 'mobx';
import * as merge from 'deepmerge';
import { getCourses } from '../../../services/CourseService';
import {
  createVTClass,
  getVTClassByUUID,
  updateVTClass,
} from '../../../services/VTClassService';
import { getLMSClassByOfferingId } from '../../../services/LMSClassService';
import { getVTCAccounts } from '../../../services/VTCAccountService';
import { getUsersInGroup } from '../../../services/UserService';

class EditClassStore {
  @observable uiSchema = {};

  @observable schema = {};

  @observable formData = {
    product: 'vt',
    instructor: {},
  };

  @observable courses = [];

  @observable users = [];

  @observable catalogStore = {};

  @observable language = 'en-US';

  @observable classesStore = {};

  @observable isSubmittedEditClass = false;

  constructor(catalogStore, classesStore) {
    this.catalogStore = catalogStore;
    this.classesStore = classesStore;
  }

  static formatDate(dateString) {
    try {
      return new Date(dateString).toISOString();
    } catch (error) {
      return null;
    }
  }

  static formatData(data = {}) {
    data.course = data.course.uuid;
    data.start_time = this.formatDate(data.start_time);
    data.end_time = this.formatDate(data.end_time);

    return data;
  }

  static disableLabAutoStart(lmsClassData = {}) {
    const labRequired = lmsClassData?.attr_map?.attr_virtual_lab_required;
    if (labRequired && ['y', 'yes'].includes(labRequired.toLowerCase())) {
      return false;
    }
    return true;
  }

  @action populateFields = async (uuid) => {
    const courses = await getCourses();
    const accounts = await getVTCAccounts();

    const users = this.classesStore?.instructors.length
      ? this.classesStore?.instructors
      : await getUsersInGroup('Instructors');

    this.users = users;

    const catalogEntries = await this.catalogStore.getAllCatalogEntries();
    const courseUUIDs = courses.map((course) => course.uuid);
    const courseNames = courses.map((course) => {
      const catalogEntry = catalogEntries.find(
        (entry) =>
          entry.code.toLowerCase() === course.code.toLowerCase() &&
          this.language === entry.language,
      );
      return `${course.code.toLowerCase()}-${course.version}: ${
        catalogEntry?.title || course.name
      }`;
    });

    const accountUUIDs = accounts.map((account) => account.uuid);
    const accountNames = accounts.map((account) => account.name);

    const usernames = users.map((user) => user.username);
    const userNames = users.map((user) => {
      const { firstName, lastName, username } = user;
      return firstName || lastName
        ? `${firstName} ${lastName} (${username})`
        : username;
    });

    const updatedSchema = {
      properties: {
        course: {
          enum: courseUUIDs,
          enumNames: courseNames,
        },
        instructor: {
          properties: {
            username: {
              enum: usernames,
              enumNames: userNames,
            },
          },
        },
        vtc_account: {
          enum: accountUUIDs,
          enumNames: accountNames,
        },
      },
    };

    this.schema = merge(this.schema, updatedSchema);

    if (!uuid) {
      return;
    }

    const data = await getVTClassByUUID(uuid);
    if (!data) {
      return;
    }

    const { slug } = data;

    const offeringId = slug && parseInt(slug.match(/\d+/g), 10);

    this.formData = merge(this.formData, EditClassStore.formatData(data));

    const lmsClassData = await getLMSClassByOfferingId(offeringId);
    const { sync } = lmsClassData;
    const labNotRequired = EditClassStore.disableLabAutoStart(lmsClassData);

    this.uiSchema = merge(this.uiSchema, {
      product: {
        'ui:disabled': sync,
      },
      timezone: {
        'ui:disabled': sync,
      },
      lab_auto_start: {
        'ui:disabled': labNotRequired,
      },
    });
  };

  @action updateCourseTitles = async (language) => {
    const catalogEntries = await this.catalogStore.getAllCatalogEntries();
    const newNames = this.schema.properties.course.enumNames.map((value) => {
      const currentTitle = value.split(':')[1];
      const newTitle =
        catalogEntries.find(
          (entry) =>
            entry.code.toLowerCase() === value.split('-')[0] &&
            language === entry.language,
        )?.title || currentTitle;
      return `${value.split(':')[0]}: ${newTitle}`;
    });
    this.schema.properties.course.enumNames = newNames;
  };

  @action post(formData) {
    this.isSubmittedEditClass = true;
    return new Promise((resolve, reject) => {
      createVTClass(formData)
        .then((data) => {
          if (!data) {
            return;
          }

          this.formData = merge(this.formData, EditClassStore.formatData(data));

          resolve(data);
        })
        .catch((error) => reject(error))
        .finally(() => {
          this.isSubmittedEditClass = false;
        });
    });
  }

  @action put(uuid, formData) {
    this.isSubmittedEditClass = true;
    return updateVTClass(uuid, formData)
      .then((data) => {
        if (data) {
          this.formData = merge(this.formData, EditClassStore.formatData(data));
        }
        return data;
      })
      .finally(() => {
        this.isSubmittedEditClass = false;
      });
  }
}

export default EditClassStore;
